// src/recording/session/activeMeasurementSession.js

/**
 * In-memory, app-lifetime session store enforcing mutual exclusivity
 * across measurement modes.
 *
 * "Session" here is purely UI/runtime state (not persisted).
 */

const listeners = new Set();
const SESSION_KEY = "pdx.activeMeasurementSession.v1";

function readPersistedSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === "object" &&
      typeof parsed.id === "string" &&
      typeof parsed.mode === "string" &&
      typeof parsed.startedAt === "number"
    ) {
      return parsed;
    }
  } catch {
    // ignore broken or unavailable session storage
  }

  return null;
}

function persistSession(value) {
  try {
    if (!value) {
      sessionStorage.removeItem(SESSION_KEY);
      return;
    }

    sessionStorage.setItem(SESSION_KEY, JSON.stringify(value));
  } catch {
    // ignore broken or unavailable session storage
  }
}

function uuid() {
  // lightweight id is fine; doesn't need crypto-grade uniqueness
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const state = {
  active: readPersistedSession(), // { id, mode, startedAt, reasonEnded? }
};

function emit() {
  for (const fn of listeners) fn(state.active);
}

function commit(next) {
  state.active = next;
  persistSession(next);
  emit();
}

export const ActiveMeasurementSessionStore = {
  get() {
    return state.active;
  },

  subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  /**
   * Begin a new mode session.
   * If another mode is active, it is ended/invalidated first.
   */
  begin(mode) {
    const prev = state.active;

    if (prev && prev.mode !== mode) {
      // end previous session due to mode switch
      commit({
        ...prev,
        reasonEnded: "switched_mode",
        endedAt: Date.now(),
      });

      // now clear it (invalidate)
      commit(null);
    }

    // (Re)start session for requested mode
    commit({
      id: uuid(),
      mode,
      startedAt: Date.now(),
    });

    return state.active;
  },

  /**
   * Ends the current session (if any).
   */
  end(reason = "ended") {
    const prev = state.active;
    if (!prev) return;

    commit({
      ...prev,
      reasonEnded: reason,
      endedAt: Date.now(),
    });

    // invalidate immediately (so guards block access)
    commit(null);
  },

  /**
   * Hard invalidate without emitting an "ended" snapshot first.
   */
  invalidate() {
    commit(null);
  },
};
